import * as d3 from 'd3'

// 事件處理類
export class EventHandler {
  constructor (DOM, state, vm, iconLayer, infoWindowManager, iconHandler, g) {
    this.DOM = DOM
    this.state = state
    this.vm = vm
    this.iconLayer = iconLayer
    this.infoWindowManager = infoWindowManager
    this.iconHandler = iconHandler
    this.g = g
  }

  // 註冊縣市路徑事件
  registerCountyEvents (countyPaths, tooltipManager, zoomHandler, path, width, height, zoom, clickedFunction) {
    countyPaths
      .on('mouseover', (event, d) => this.handleCountyMouseOver(event, d, tooltipManager))
      .on('mousemove', (event) => this.handleCountyMouseMove(event))
      .on('mouseout', (event, d) => this.handleCountyMouseOut(event, d, tooltipManager))
      .on('click', clickedFunction)
  }

  // 處理縣市滑鼠懸停
  handleCountyMouseOver (event, d, tooltipManager) {
    if (this.state.isLocked) return

    const countyName = d.properties.COUNTYNAME
    const count = this.vm.siteData.filter(site => site.county === countyName).length
    tooltipManager.showCountyTooltip(`${count}個案場`)

    d3.select(event.currentTarget)
      .attr('fill', '#0582f7')
      .attr('filter', 'drop-shadow(0px 0px 10px rgba(255,255,255,0.2))')
      .attr('cursor', 'pointer')

    if (this.g) {
      this.g.selectAll('text.town-label')
        .filter(label => label.name === countyName)
        .attr('fill', '#e9e9e9')
    }
  }

  // 處理縣市滑鼠移動
  handleCountyMouseMove (event) {
    const offsetX = -95
    const offsetY = -130
    this.DOM.countyTooltip.style.left = `${event.pageX + offsetX}px`
    this.DOM.countyTooltip.style.top = `${event.pageY + offsetY}px`
  }

  // 處理縣市滑鼠離開
  handleCountyMouseOut (event, d, tooltipManager) {
    if (this.state.isLocked) return
    const countyName = d.properties.COUNTYNAME

    d3.select(event.currentTarget)
      .attr('fill', '#404457')
      .attr('filter', null)
      .attr('cursor', null)

    if (this.g) {
      this.g.selectAll('text.town-label')
        .filter(label => label.name === countyName)
        .attr('fill', '#6e748f')
    }
    tooltipManager.hideCountyTooltip()
  }

  // 處理縣市點擊
  handleCountyClick (d, zoomHandler, path, width, height, zoom) {
    if (this.state.isLocked) return
    this.state.isLocked = true

    this.DOM.countyTooltip.style.display = 'none'

    // 更新縣市狀態
    this.vm.selectedCounty = {
      zh: d.properties.COUNTYNAME,
      en: d.properties.ENGLISHNAME.toUpperCase()
    }

    // 聚焦到縣市
    zoomHandler.focusOnCounty(d, path, width, height, zoom)

    // 顯示相關 UI 元素
    this.showCountyUI()

    // 取得該縣市所有站點
    const sitesInCounty = this.vm.siteData.filter(site => site.county === d.properties.COUNTYNAME)

    return sitesInCounty
  }

  // 顯示縣市相關 UI
  showCountyUI () {
    if (this.DOM.replyButton) this.DOM.replyButton.style.display = 'block'
    if (this.DOM.mapTitleV2) this.DOM.mapTitleV2.style.display = 'block'
    if (this.DOM.statusTable) this.DOM.statusTable.style.display = 'block'
    if (this.DOM.mapTitle) this.DOM.mapTitle.style.display = 'none'
  }

  // 註冊全域點擊事件
  registerGlobalClickEvents () {
    // 資訊視窗點擊事件
    this.DOM.infoWindow.addEventListener('click', (e) => e.stopPropagation())

    // 全域點擊事件 - 使用 capture: true 確保事件能被捕獲
    document.addEventListener('click', (event) => {
      this.handleGlobalClick(event)
    }, { capture: true })

    // 也為 SVG 元素添加點擊事件
    const svg = document.querySelector('svg')
    if (svg) {
      svg.addEventListener('click', (event) => {
        this.handleGlobalClick(event)
      })
    }
  }

  // 處理全域點擊
  handleGlobalClick (event) {
    const isClickOutsideInfoWindow = !this.DOM.infoWindow.contains(event.target)
    const modalBk = document.querySelector('.modal-bk')
    const modalMain = document.querySelector('.modal')
    const isClickInsideModal =
      (modalBk && modalBk.contains(event.target)) ||
      (modalMain && modalMain.contains(event.target))

    if (isClickInsideModal) return

    // 檢查是否點擊在母圖標上
    const isParentIcon = event.target && event.target.classList && (
      event.target.classList.contains('progress-bg') ||
      event.target.classList.contains('progress-ring') ||
      event.target.classList.contains('image-hover-shadow')
    )

    // 檢查是否點擊在子圖標上
    const isChildIcon = event.target && event.target.classList && (
      event.target.classList.contains('child-icon')
    )

    // 檢查是否點擊在當前展開的母圖標上
    // 注意：data-expanded 是設置在 g 元素上，不是 image 元素上
    const parentGroup = event.target.closest('[data-site-id]')
    const isExpandedParentIcon = isParentIcon && parentGroup && parentGroup.getAttribute('data-expanded') === 'true'

    // 如果點擊在子圖標上，不處理收合
    if (isChildIcon) return

    // 如果點擊在當前展開的母圖標上，不處理收合（讓 handleParentClick 處理）
    if (isExpandedParentIcon) return

    // 如果正在點擊圖標，立即重置狀態並處理收合
    if (this.state.isClickingIcon) {
      this.state.isClickingIcon = false
      // 繼續執行收合邏輯
    }

    // 處理子圖標收合
    if (this.state.hasChildIcons && isClickOutsideInfoWindow) {
      this.collapseChildIconsWithAnimation()
    }

    // 處理資訊視窗關閉
    if (this.DOM.infoWindow.style.display === 'block' && isClickOutsideInfoWindow) {
      this.infoWindowManager.fadeOutInfoWindow()
    }
  }

  // 收合子圖標（帶動畫）
  collapseChildIconsWithAnimation () {
    // 找到當前展開的母圖標組（g 元素）
    const expandedIconGroup = this.iconLayer.selectAll('[data-site-id][data-expanded="true"]')

    if (expandedIconGroup.size()) {
      // 從母圖標組的 data-site-id 屬性獲取站點資訊
      const siteId = expandedIconGroup.attr('data-site-id')

      // 從 vm.siteData 中找到對應的站點數據
      let foundSiteInfo = null
      if (this.vm.siteData && siteId) {
        foundSiteInfo = this.vm.siteData.find(site => {
          const currentSiteId = site.id || `${site.county}-${site.lat}-${site.lon}`
          return currentSiteId === siteId
        })
      }

      if (foundSiteInfo) {
        // 使用 iconHandler 的收合動畫方法
        if (this.iconHandler && this.iconHandler.collapseChildIcons) {
          this.iconHandler.collapseChildIcons(foundSiteInfo)
        }
      }

      this.state.hasChildIcons = false // 交由單一入口決定標籤顯示
      this.iconHandler.updateAllLabelsVisibility()
    }

    // 重置狀態
    this.iconLayer.selectAll('image')
      .attr('data-expanded', null)
      .classed('image-focus-shadow', false)
    this.iconLayer.selectAll('.progress-bg')
      .classed('image-focus-shadow', false)
    this.iconLayer.selectAll('[data-site-id]')
      .attr('data-expanded', null)
    this.state.hasChildIcons = false
  }
}
